import { useLanguage } from "../context/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";

const Sparkle = ({ className = "" }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M10 0L11.5 8.5L20 10L11.5 11.5L10 20L8.5 11.5L0 10L8.5 8.5L10 0Z"
      fill="#C5A059"
    />
  </svg>
);

const Hero = () => {
  const { t, isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
    >
      {/* Decorative pastel blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-20 -end-32 w-[480px] h-[480px] rounded-full opacity-60 blur-3xl"
        style={{ background: "var(--p-collagen)" }}
      />
      <div
        aria-hidden="true"
        className="absolute top-40 -start-20 w-[360px] h-[360px] rounded-full opacity-50 blur-3xl"
        style={{ background: "var(--p-immunity)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        {/* Text column */}
        <div className="lg:col-span-7 relative">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--bg-subtle)] border border-[var(--brand-gold)]/20 mb-6"
            data-testid="hero-eyebrow"
          >
            <Sparkle className="w-3 h-3" />
            <span className="text-xs tracking-widest uppercase text-[var(--text-secondary)]">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1
            className="font-display text-[56px] leading-[1.02] sm:text-[72px] lg:text-[88px] xl:text-[104px] font-medium text-[var(--text-primary)]"
            data-testid="hero-title"
          >
            <span className="block">{t.hero.tagline_1}</span>
            <span
              className="block italic text-[var(--brand-gold)]"
              style={{ fontVariationSettings: "'opsz' 144" }}
            >
              {t.hero.tagline_2}
            </span>
            <span className="block">{t.hero.tagline_3}</span>
          </h1>

          <p
            className="mt-8 max-w-xl text-base md:text-lg text-[var(--text-secondary)] leading-relaxed"
            data-testid="hero-subheading"
          >
            {t.hero.subheading}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#products"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-7 h-14 rounded-full bg-[var(--text-primary)] text-[var(--bg-base)] text-sm font-medium hover:bg-[var(--brand-gold)]"
            >
              {t.hero.cta_primary}
              <Arrow className="w-4 h-4" />
            </a>
            <a
              href="#mission"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 h-14 rounded-full border border-[var(--text-primary)]/15 hover:border-[var(--brand-gold)] text-sm font-medium"
            >
              {t.hero.cta_secondary}
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-stats">
            {[
              { v: t.hero.stat_1, l: t.hero.stat_1_label },
              { v: t.hero.stat_2, l: t.hero.stat_2_label },
              { v: t.hero.stat_3, l: t.hero.stat_3_label },
            ].map((s, i) => (
              <div key={i} className="border-s border-[var(--brand-gold)]/40 ps-4">
                <div className="font-display text-2xl md:text-3xl text-[var(--text-primary)]">
                  {s.v}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image column */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] max-w-[500px] mx-auto">
            {/* Soft pastel disc behind */}
            <div
              className="absolute inset-6 rounded-[40%_60%_55%_45%/45%_50%_50%_55%] blur-2xl opacity-80"
              style={{
                background:
                  "linear-gradient(135deg, var(--p-happy), var(--p-collagen) 60%, var(--p-antioxidant))",
              }}
            />
            <img
              src="https://customer-assets.emergentagent.com/job_d8bfe459-56db-4669-bf31-d95e5b70b8e8/artifacts/ibpribsk_IMG_8653.png"
              alt="Saha Lab Vitamin Water"
              data-testid="hero-image-primary"
              className="relative w-full h-full object-cover rounded-[36px] shadow-[0_30px_80px_-30px_rgba(44,48,46,0.35)]"
              style={{ filter: "blur(14px) saturate(1.05)" }}
            />
            {/* Coming Soon overlay */}
            <div
              data-testid="hero-coming-soon-overlay"
              className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            >
              <div className="bg-[var(--text-primary)]/85 backdrop-blur-md px-7 py-4 rounded-full border border-[var(--brand-gold)]/40 shadow-2xl">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-[var(--brand-gold)]" />
                  <span className="font-display text-lg md:text-xl text-[var(--bg-base)] tracking-wide">
                    {t.hero.coming_soon_badge}
                  </span>
                </div>
              </div>
            </div>
            {/* Sparkle decorations */}
            <Sparkle className="absolute -top-4 -start-4 w-6 h-6" />
            <Sparkle className="absolute top-1/3 -end-3 w-4 h-4" />
            <Sparkle className="absolute -bottom-2 start-1/3 w-5 h-5" />

            {/* Floating badge */}
            <div className="absolute -bottom-6 start-4 bg-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3 border border-[var(--bg-subtle)]">
              <div className="w-2 h-2 rounded-full bg-[var(--brand-gold)]" />
              <div className="text-xs">
                <div className="font-semibold text-[var(--text-primary)]">
                  Vitamin Water
                </div>
                <div className="text-[var(--text-secondary)]">
                  {t.hero.coming_soon_badge}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
